import "phoenix_html";
// Establish Phoenix Socket and LiveView configuration.
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import topbar from "../vendor/topbar";
import { platform } from "./platform";
import { enableActivityTracking } from "./activity_tracking";
import { enableReconnectOnTabResume } from "./reconnect_on_tab_resume";
import { CopyOnClick } from "./phx-hooks/copy_on_click";
import { FocusFirstInputOnMount } from "./phx-hooks/focus_first_input_on_mount";
import { DispatchEvent } from "./phx-hooks/dispatch_event";
import { DocumentPanZoom } from "./phx-hooks/document_pan_zoom";
import { OnMouseDown } from "./phx-hooks/on_mouse_down.js";
import { PositionDraggable } from "./phx-hooks/position_draggable";
import { PreventLinkClick } from "./phx-hooks/prevent_link_click";
import { SubmitOnEnter } from "./phx-hooks/submit_on_enter";
import { SetRelativePosition } from "./phx-hooks/set_relative_position";
import { KeyboardListener } from "./phx-hooks/keyboard_listener";
import { Tippy } from "./phx-hooks/tippy_hook";
import { EnsureInViewWhenLetterImagesLoaded } from "./phx-hooks/tasks/ensure_in_view_when_letter_images_loaded";
import { FormatTimeInLocalTimezone } from "./phx-hooks/format_time_in_local_timezone";
import { AdvancedMultiSelect } from "./phx-hooks/advanced_multi_select";
import { LetterPreviewOnHover } from "./phx-hooks/letter_preview_on_hover";
import { PageScrollObserver } from "./phx-hooks/page_scroll_observer.js";
import { PrettyJson } from "./phx-hooks/pretty_json.js";
import { RefererStorage } from "./phx-hooks/referer/referer_storage";
import { ZipUpload } from "./phx-hooks/zip_upload.js";
import { hooks as colocatedHooks } from "phoenix-colocated/mailroom";

let csrfToken = document
  .querySelector("meta[name='csrf-token']")
  .getAttribute("content");

let liveSocket = new LiveSocket("/live", Socket, {
  longPollFallbackMs: 5000,
  hooks: {
    CopyOnClick: CopyOnClick,
    FocusFirstInputOnMount: FocusFirstInputOnMount,
    DispatchEvent: DispatchEvent,
    DocumentPanZoom: DocumentPanZoom,
    OnMouseDown: OnMouseDown,
    PositionDraggable: PositionDraggable,
    PreventLinkClick: PreventLinkClick,
    SubmitOnEnter: SubmitOnEnter,
    SetRelativePosition: SetRelativePosition,
    KeyboardListener: KeyboardListener,
    Tippy: Tippy,
    EnsureInViewWhenLetterImagesLoaded: EnsureInViewWhenLetterImagesLoaded,
    FormatTimeInLocalTimezone: FormatTimeInLocalTimezone,
    AdvancedMultiSelect: AdvancedMultiSelect,
    PageScrollObserver: PageScrollObserver,
    PrettyJson: PrettyJson,
    RefererStorage: RefererStorage,
    ZipUpload: ZipUpload,
    LetterPreviewOnHover: LetterPreviewOnHover,
    ...colocatedHooks,
  },
  params: { _csrf_token: csrfToken, platform: platform() },
  metadata: {
    keydown: (e, el) => {
      return {
        key: e.key,
        metaKey: e.metaKey,
        repeat: e.repeat,
      };
    },
    click: (e, el) => {
      return {
        altKey: e.altKey,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        metaKey: e.metaKey,
      };
    },
  },
});

// https://fly.io/phoenix-files/make-your-liveview-feel-faster/
// Show progress bar on live navigation and form submits. Only displays if still loading after 200 msec
topbar.config({ barColors: { 0: "#29d" }, shadowColor: "rgba(0, 0, 0, .3)" });

let topBarScheduled = undefined;
window.addEventListener("phx:page-loading-start", () => {
  if (!topBarScheduled) {
    topBarScheduled = setTimeout(() => topbar.show(), 200);
  }
});
window.addEventListener("phx:page-loading-stop", () => {
  clearTimeout(topBarScheduled);
  topBarScheduled = undefined;
  topbar.hide();
});

// connect if there are any LiveViews on the page
liveSocket.connect();

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket;

enableActivityTracking(csrfToken);
enableReconnectOnTabResume();

window.downloadFile = (url, fileName) => {
  location.href = url + `?filename=${fileName}`;
};

window.addEventListener("phx:mailroom:download-file", (event) => {
  window.downloadFile(event.detail.url, event.detail.file_name);
});

window.addEventListener("phx:mailroom:download-csv", (event) => {
  // The BOM makes Excel read the file as UTF-8 instead of the system code page.
  const blob = new Blob(["\uFEFF", event.detail.content], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = event.detail.filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
});

window.addEventListener("phx:mailroom:set-text-if-focused", (event) => {
  const input = document.getElementById(event.detail.to);
  if (!input) return;
  if (input != document.activeElement) return;
  if (input.value) return;

  input.value = event.detail.text;
});

window.addEventListener("phx:history-back", (event) => {
  if (history.length > 1) {
    history.back();
  } else {
    location.href = event.detail.fallback;
  }
});

window.addEventListener("phx:update_input_value", async (event) => {
  const input = document.getElementById(event.detail.id);

  input.value = event.detail.value;
  input.dispatchEvent(new Event("input", { bubbles: true }));
});

window.addEventListener("phx:scroll_to", async (event) => {
  const elem = document.getElementById(event.detail.id);

  if (elem != null) {
    const { top, bottom } = elem.getBoundingClientRect();
    const { innerHeight } = window;
    const topBarHeight = 64;
    const bottomBarHeight = 64;

    if (top < topBarHeight || bottom >= innerHeight - bottomBarHeight) {
      setTimeout(() => {
        elem.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    }
  }
});

window.addEventListener("phx:scroll_to_and_focus", async (event) => {
  const elem = document.getElementById(event.detail.id);

  if (elem != null) {
    parentTable = elem.closest("table");

    if (parentTable != null) {
      parentTable.scrollIntoView({ block: "nearest", behavior: "smooth" });
      elem.focus({ preventScroll: true });
    } else {
      elem.scrollIntoView({ block: "nearest", behavior: "smooth" });
      elem.focus({ preventScroll: true });
      elem.classList.add("animate-bounce");
      setTimeout(() => {
        elem.classList.remove("animate-bounce");
      }, 2500);
    }
  }
});

window.addEventListener("phx:flash_new_row", (event) => {
  const row = document.getElementById(event.detail.id)?.closest("tr");
  if (row == null) return;

  for (const cell of row.children) {
    cell.animate(
      [
        { backgroundColor: "rgba(104, 231, 209, 0.6)" },
        { backgroundColor: "rgba(104, 231, 209, 0)" },
      ],
      { duration: 1500, easing: "ease-out" },
    );
  }
});

window.addEventListener("phx:focus", async (event) => {
  const elem = document.getElementById(event.detail.id);
  if (elem != null) {
    elem.focus();
  }
});

window.addEventListener("phx:js-exec", ({ detail }) => {
  setTimeout(() => {
    if (detail.target_selector != null) {
      document.querySelectorAll(detail.target_selector).forEach((el) => {
        const js = detail.js || el.getAttribute(detail.attr);
        liveSocket.execJS(el, js);
      });
    } else {
      liveSocket.execJS(document.body, detail.js);
    }
  }, 200);
});

window.addEventListener("phx:visualise_cell_update", (event) => {
  for (const cellId of event.detail.cells) {
    setTimeout(
      () => {
        const cell = document.getElementById(cellId);

        if (cell) {
          cell.style.transition = "transform 0.3s ease";
          cell.style.transform = "translateY(-3px)";

          setTimeout(() => {
            cell.style.transform = "translateY(0px)";
          }, 200);
        }
      },
      // delay the start of animation randomly and slightly to avoid conflicting with Phoenix updates and to get a bit of a jittery effect
      200 * Math.random() + 100,
    );
  }
});
